<template>
  <div>


    <!-- Conversations Table -->
    <v-card class="elevation-2">
      <v-card-title class="text-h6">
        Voice Agent Conversations ({{totalCount}})
        <v-spacer></v-spacer>
        <v-chip v-if="loading" color="orange" text-color="white" small>
          <v-icon left small class="material-icons-round">refresh</v-icon>
          Loading...
        </v-chip>
      </v-card-title>

      <v-data-table
        :headers="headers_conversations"
        :items="conversations"
        :loading="loading"
        loading-text="Loading conversations..."
        class="elevation-0"
        :items-per-page="10"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100]
        }"
      >
        <template v-slot:item.call_started_at="{ item }">
          {{formatDate(item.call_started_at)}}
        </template>
        
        <template v-slot:item.duration="{ item }">
          <v-chip small color="blue-grey" text-color="white">
            {{item.formatted_duration || 'N/A'}}
          </v-chip>
        </template>
        
        <template v-slot:item.lead_name="{ item }">
          <span v-if="item.user">
            <v-icon small class="mr-1 material-icons-round">person</v-icon>
            {{item.user.first_name}} {{item.user.last_name}}
          </span>
          <span v-else-if="item.custom_analysis_data && item.custom_analysis_data.customer_name">
            <v-icon small class="mr-1 material-icons-round">person_outline</v-icon>
            {{item.custom_analysis_data.customer_name}}
          </span>
          <span v-else class="text--secondary">
            <v-icon small class="mr-1 material-icons-round">person_outline</v-icon>
            No lead created
          </span>
        </template>
        
        <template v-slot:item.status="{ item }">
          <v-chip 
            :color="getStatusColor(item.status)" 
            text-color="white" 
            small
          >
            {{item.status}}
          </v-chip>
        </template>
        
        <template v-slot:item.phone_numbers="{ item }">
          <div class="text-caption">
            <div><strong>From:</strong> {{item.from_number || 'N/A'}}</div>
            <div><strong>To:</strong> {{item.to_number || 'N/A'}}</div>
          </div>
        </template>
        
        <template v-slot:item.summary="{ item }">
          <div style="max-width: 250px;" class="text-truncate">
            {{item.call_analysis && item.call_analysis.call_summary ? item.call_analysis.call_summary : (item.conversation_summary || item.summary || 'No summary available')}}
          </div>
        </template>
        
        <template v-slot:item.actions="{ item }">
          <v-btn
            icon
            small
            color="primary"
            @click="viewConversation(item)"
            title="View Full Conversation"
          >
            <v-icon small class="material-icons-round">visibility</v-icon>
          </v-btn>
          <v-btn
            v-if="item.user"
            icon
            small
            color="success"
            @click="viewLead(item.user)"
            title="View Lead Profile"
          >
            <v-icon small class="material-icons-round">person</v-icon>
          </v-btn>
        </template>
      </v-data-table>
    </v-card>

    <!-- Conversation Detail Dialog -->
    <v-dialog v-model="conversationDialog" max-width="900px" scrollable>
      <v-card>
        <v-card-title class="text-h5 primary white--text">
          <v-icon class="mr-2 material-icons-round" color="white">phone</v-icon>
          Conversation Details
          <v-spacer></v-spacer>
          <v-btn icon color="white" @click="conversationDialog = false">
            <v-icon class="material-icons-round">close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-0" v-if="selectedConversation">
          <!-- Call Information -->
          <v-container class="py-4">
            <v-row>
              <v-col cols="12" md="6">
                <div class="mb-2">
                  <strong>Call ID:</strong> {{selectedConversation.call_id}}
                </div>
                <div class="mb-2">
                  <strong>Duration:</strong> 
                  <v-chip small color="blue-grey" text-color="white">
                    {{selectedConversation.formatted_duration || 'N/A'}}
                  </v-chip>
                </div>
                <div class="mb-2">
                  <strong>Status:</strong> 
                  <v-chip :color="getStatusColor(selectedConversation.status)" text-color="white" small>
                    {{selectedConversation.status}}
                  </v-chip>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="mb-2">
                  <strong>From:</strong> {{selectedConversation.from_number || 'N/A'}}
                </div>
                <div class="mb-2">
                  <strong>To:</strong> {{selectedConversation.to_number || 'N/A'}}
                </div>
                <div class="mb-2">
                  <strong>Started:</strong> {{formatDateTime(selectedConversation.call_started_at)}}
                </div>
              </v-col>
            </v-row>
          </v-container>

          <v-divider></v-divider>

          <!-- Lead Information -->
          <v-container v-if="selectedConversation.user || selectedConversation.custom_analysis_data" class="py-4">
            <h6 class="text-h6 mb-3">
              <v-icon class="mr-2">person</v-icon>
              Lead Information
            </h6>
            <v-row v-if="selectedConversation.user">
              <v-col cols="12" md="4">
                <strong>Name:</strong> {{selectedConversation.user.first_name}} {{selectedConversation.user.last_name}}
              </v-col>
              <v-col cols="12" md="4">
                <strong>Email:</strong> {{selectedConversation.user.email || 'N/A'}}
              </v-col>
              <v-col cols="12" md="4">
                <strong>Phone:</strong> {{selectedConversation.user.phone_m || 'N/A'}}
              </v-col>
            </v-row>
            <v-row v-else-if="selectedConversation.custom_analysis_data">
              <v-col cols="12" md="4">
                <strong>Name:</strong> {{selectedConversation.custom_analysis_data.customer_name || 'N/A'}}
              </v-col>
              <v-col cols="12" md="4">
                <strong>Email:</strong> {{selectedConversation.custom_analysis_data.customer_email || 'N/A'}}
              </v-col>
              <v-col cols="12" md="4">
                <strong>Phone:</strong> {{selectedConversation.custom_analysis_data.customer_phone || 'N/A'}}
              </v-col>
            </v-row>
            <v-row v-if="selectedConversation.custom_analysis_data">
              <v-col cols="12" md="4">
                <strong>Interest:</strong> {{selectedConversation.custom_analysis_data.lead_interest || 'N/A'}}
              </v-col>
              <v-col cols="12" md="4">
                <strong>Referrer:</strong> {{selectedConversation.custom_analysis_data.referrer_name || 'N/A'}}
              </v-col>
              <v-col cols="12" md="4">
                <strong>Vehicle Type:</strong> {{selectedConversation.custom_analysis_data.vehicle_type || 'N/A'}}
              </v-col>
            </v-row>
          </v-container>

          <v-divider v-if="selectedConversation.user"></v-divider>

          <!-- Conversation Transcript -->
          <v-container class="py-4">
            <h6 class="text-h6 mb-3">
              <v-icon class="mr-2 material-icons-round">chat</v-icon>
              Conversation Transcript
            </h6>
            
            <div v-if="selectedConversation.parsed_transcript && selectedConversation.parsed_transcript.length > 0"
                 class="conversation-transcript">
              <div
                v-for="(message, index) in selectedConversation.parsed_transcript"
                :key="index"
                class="message-bubble mb-3"
                :class="message.role === 'user' ? 'user-message' : 'agent-message'"
              >
                <div class="message-header">
                  <v-chip
                    :color="message.role === 'user' ? 'blue' : 'purple'"
                    text-color="white"
                    x-small
                    class="mr-2"
                  >
                    {{message.role === 'user' ? 'Customer' : 'Agent'}}
                  </v-chip>
                  <small class="text--secondary" v-if="message.timestamp">{{formatTime(message.timestamp)}}</small>
                </div>
                <div class="message-content mt-2">
                  {{message.content}}
                </div>
              </div>
            </div>
            
            <div v-else class="text-center py-8">
              <v-icon size="64" color="grey lighten-2" class="material-icons-round">chat_bubble_outline</v-icon>
              <div class="text-h6 grey--text mt-2">No transcript available</div>
              <div class="text--secondary">This conversation doesn't have transcript data</div>
            </div>
          </v-container>

          <!-- Analysis Data -->
          <template v-if="selectedConversation.call_analysis">
            <v-divider></v-divider>
            <v-container class="py-4">
              <h6 class="text-h6 mb-3">
                <v-icon class="mr-2 material-icons-round">analytics</v-icon>
                AI Analysis Data
              </h6>
              <v-expansion-panels>
                <v-expansion-panel>
                  <v-expansion-panel-header>
                    <strong>View Analysis Details</strong>
                  </v-expansion-panel-header>
                  <v-expansion-panel-content>
                    <pre class="analysis-data">{{JSON.stringify(selectedConversation.call_analysis, null, 2)}}</pre>
                  </v-expansion-panel-content>
                </v-expansion-panel>
              </v-expansion-panels>
            </v-container>
          </template>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" text @click="conversationDialog = false">
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Vue from "vue";
import moment from "moment";

export default {
  name: "AgentConversations",
  data() {
    return {
      conversations: [],
      loading: false,
      search: "",
      totalCount: 0,
      conversationDialog: false,
      selectedConversation: null,
      headers_conversations: [
        { text: 'Date & Time', value: 'call_started_at', sortable: true },
        { text: 'Duration', value: 'duration', sortable: false },
        { text: 'Lead', value: 'lead_name', sortable: false },
        { text: 'Phone Numbers', value: 'phone_numbers', sortable: false },
        { text: 'Status', value: 'status', sortable: true },
        { text: 'Summary', value: 'summary', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false }
      ]
    };
  },
  mounted() {
    this.getConversations();
  },
  methods: {
    formatDate(value) {
      return moment(value).format("DD MMM YYYY HH:mm");
    },
    formatDateTime(value) {
      return moment(value).format("DD MMM YYYY HH:mm:ss");
    },
    formatTime(value) {
      return moment(value).format("HH:mm:ss");
    },
    getStatusColor(status) {
      switch(status) {
        case 'started': return 'orange';
        case 'ended': return 'blue';
        case 'analyzed': return 'green';
        default: return 'grey';
      }
    },
    getConversations() {
      this.loading = true;

      let req = {
        limit: -1, // Get all conversations for the agent
        offset: 0,
        search: this.search
      };

      axios({
        method: "POST",
        url: "/api/voice-agent-conversations",
        data: req,
        headers: {
          Authorization: "Bearer " + localStorage.getItem('token')
        }
      })
      .then(response => {
        let data = response.data;
        if (response.data.flag) {
          this.conversations = data.data;
          this.totalCount = data.count;
          this.loading = false;
        } else {
          Vue.$toast.error(data.message || 'Error loading conversations', {
            position: 'top-right'
          });
          this.loading = false;
        }
      })
      .catch(err => {
        Vue.$toast.error("Error loading conversations", {
          position: 'top-right'
        });
        this.loading = false;
      });
    },
    viewConversation(conversation) {
      this.selectedConversation = conversation;
      this.conversationDialog = true;
    },
    viewLead(user) {
      // Open lead profile in new tab
      window.open('/admin/client-profile-detail/' + user.user_id, '_blank');
    }
  }
};
</script>

<style scoped>
.conversation-transcript {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 16px;
  background-color: #fafafa;
}

.message-bubble {
  padding: 12px;
  border-radius: 8px;
  margin-bottom: 8px;
}

.user-message {
  background-color: #e3f2fd;
  border-left: 4px solid #2196f3;
}

.agent-message {
  background-color: #f3e5f5;
  border-left: 4px solid #9c27b0;
}

.message-header {
  font-size: 0.9em;
  margin-bottom: 4px;
}

.message-content {
  line-height: 1.4;
  word-wrap: break-word;
}

.analysis-data {
  background-color: #f5f5f5;
  padding: 16px;
  border-radius: 4px;
  font-size: 0.85em;
  max-height: 300px;
  overflow-y: auto;
}

.text-truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
